<template>
  <div class="op-card">
    <header class="op-header">
      <div>
        <h5 class="op-title">Performance dos operadores</h5>
        <p class="op-subtitle">
          {{ operators.length }} {{ operators.length === 1 ? 'operador ativo' : 'operadores ativos' }}
          no período
        </p>
      </div>
      <div v-if="operators.length > 1" class="op-sort-chips">
        <button
          v-for="opt in sortOptions"
          :key="opt.value"
          type="button"
          class="op-schip"
          :class="{ 'op-schip-active': sortBy === opt.value }"
          @click="sortBy = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
    </header>

    <div v-if="operators.length === 0" class="op-empty">
      <i class="bx bx-user-x op-empty-icon"></i>
      <p>Nenhum operador registrou leituras no período</p>
      <small>Os operadores aparecem aqui conforme realizam check-ins</small>
    </div>

    <div v-else class="op-list">
      <div
        v-for="(op, idx) in visibleOperators"
        :key="op.operator_id"
        class="op-item"
        :class="{ 'op-item-fade': hasMore && idx === visibleOperators.length - 1 }"
        @click="$emit('select-operator', op)"
      >
        <div class="op-row">
          <span class="op-rank">{{ idx + 1 }}</span>
          <div class="op-row-body">
            <div class="op-row-top">
              <span class="op-name" :title="op.operator_name">{{ op.operator_name }}</span>
              <span class="op-row-total">{{ op.total }}</span>
            </div>
            <div class="op-row-foot">
              <span class="op-rate">{{ op.reads_per_min }}/min</span>
              <span class="op-row-time">{{ formatLastActivity(op) }}</span>
            </div>
          </div>
        </div>

        <!-- Histograma full-width abaixo da row — substituiu a antiga barra
             de volume relativo. Mostra magnitude + tempo numa visualização só. -->
        <div
          v-if="op.histogram && op.histogram.peak_count > 0"
          class="op-histogram"
          :title="histogramTooltip(op.histogram)"
        >
          <div class="op-histogram-bars">
            <span
              v-for="b in op.histogram.buckets"
              :key="b.idx"
              class="op-histogram-bar"
              :class="{ 'op-histogram-bar-peak': b.idx === op.histogram.peak_idx }"
              :style="{ height: barHeight(b.count, op.histogram.peak_count) + '%' }"
              :title="`${b.label} · ${b.count} leituras`"
            ></span>
          </div>
          <div class="op-histogram-axis">
            <span>{{ op.histogram.buckets[0].label }}</span>
            <span class="op-histogram-axis-mid">distribuição no período</span>
            <span>{{ op.histogram.buckets[op.histogram.buckets.length - 1].label }}</span>
          </div>
        </div>
      </div>

      <button
        v-if="hasMore"
        type="button"
        class="op-show-more"
        @click="showMore"
      >
        <i class="bx bx-chevron-down"></i>
        Ver mais {{ Math.min(STEP, remaining) }}
        <span class="op-show-more-of">de {{ remaining }} restantes</span>
      </button>
    </div>
  </div>
</template>

<script>
const INITIAL_VISIBLE = 5
const STEP = 5

export default {
  name: 'operator-performance',
  props: {
    operators: { type: Array, default: () => [] }
  },
  data: () => ({
    sortBy: 'total',
    sortOptions: [
      { value: 'total', label: 'Volume' },
      { value: 'rate', label: 'Velocidade' }
    ],
    visibleCount: INITIAL_VISIBLE,
    STEP
  }),
  watch: {
    /**
     * Reseta a paginação quando muda o critério de ordenação ou quando
     * o conjunto de operadores muda de tamanho. Mantém expandido em
     * polling do mesmo conjunto pra não "saltar" pra cima sozinho.
     */
    sortBy () { this.visibleCount = INITIAL_VISIBLE },
    operators (newVal, oldVal) {
      if (!oldVal || newVal.length !== oldVal.length) {
        this.visibleCount = INITIAL_VISIBLE
      }
    }
  },
  computed: {
    sorted () {
      const arr = [...this.operators]
      arr.sort((a, b) => {
        if (this.sortBy === 'rate') return (b.reads_per_min || 0) - (a.reads_per_min || 0)
        return (b.total || 0) - (a.total || 0)
      })
      return arr
    },
    visibleOperators () {
      return this.sorted.slice(0, this.visibleCount)
    },
    hasMore () {
      return this.sorted.length > this.visibleCount
    },
    remaining () {
      return Math.max(0, this.sorted.length - this.visibleCount)
    },
    maxValue () {
      if (this.sorted.length === 0) return 1
      const top = this.sorted[0]
      return this.sortBy === 'rate' ? (top.reads_per_min || 1) : (top.total || 1)
    }
  },
  methods: {
    relativePct (op) {
      const v = this.sortBy === 'rate' ? op.reads_per_min : op.total
      return Math.max(3, Math.min(100, Math.round((v / this.maxValue) * 100)))
    },
    formatLastActivity (op) {
      if (!op.last_event_at) return '—'
      const last = new Date(op.last_event_at)
      const diffMin = Math.floor((Date.now() - last.getTime()) / 60000)
      if (diffMin < 1) return 'agora'
      if (diffMin < 60) return `há ${diffMin}min`
      const h = Math.floor(diffMin / 60)
      return `há ${h}h`
    },
    /**
     * Altura relativa de cada barra do mini histograma.
     * Mínimo de 6% pra que buckets com 0 ainda apareçam visualmente como "linha base".
     */
    barHeight (count, peak) {
      if (!peak) return 6
      if (count === 0) return 6
      const pct = (count / peak) * 100
      return Math.max(15, Math.min(100, pct))
    },
    histogramTooltip (h) {
      const peak = h.buckets[h.peak_idx]
      if (!peak) return ''
      return `Pico em ${peak.label} (${peak.count} leituras)`
    },
    showMore () {
      this.visibleCount += STEP
    }
  }
}
</script>

<style lang="scss" scoped>
.op-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.op-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.op-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.op-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
}

.op-sort-chips {
  display: inline-flex;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 2px;
  gap: 1px;
}

.op-schip {
  background: transparent;
  border: 0;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  &:hover { background: #fff; }
  &.op-schip-active {
    background: #fff;
    color: #556ee6;
    box-shadow: 0 1px 3px rgba(26, 29, 38, 0.08);
  }
}

.op-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;
  text-align: center;
  padding: 32px 24px;

  .op-empty-icon { font-size: 42px; color: #d0d5dd; margin-bottom: 8px; }
  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; }
}

.op-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.op-item {
  padding: 12px 4px;
  cursor: pointer;
  border-radius: 5px;
  transition: background 0.15s ease;
  position: relative;

  & + .op-item { border-top: 1px solid #f0f1f6; }

  &:hover { background: #fafbfc; }

  /* Fade gradient no último item visível quando há mais ocultos */
  &.op-item-fade::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 50%;
    background: linear-gradient(to bottom, transparent, #fff 90%);
    pointer-events: none;
    z-index: 1;
  }
}

.op-show-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  padding: 10px 14px;
  background: #fafbfc;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  color: #556ee6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 18px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #fff;
    border-color: #c5caf5;
    box-shadow: 0 2px 8px rgba(85, 110, 230, 0.08);

    i { transform: translateY(2px); }
  }
}

.op-show-more-of {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
  margin-left: 4px;
}

.op-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 8px;
}

.op-rank {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: #f0f1f6;
  color: #4a5068;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.op-row-body {
  flex: 1;
  min-width: 0;
}

.op-row-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.op-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.op-row-total {
  font-size: 16px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  letter-spacing: -0.02em;
}

.op-row-foot {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
}

.op-rate {
  color: #556ee6;
  font-weight: 600;
}

/* Histograma full-width abaixo da row — substituiu a antiga barra de
   volume relativo. Comunica magnitude + tempo numa visualização só. */
.op-histogram {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 44px; /* alinha com o body, fora da coluna do rank */
}

.op-histogram-bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 28px;
}

.op-histogram-bar {
  flex: 1;
  min-width: 4px;
  background: rgba(176, 181, 200, 0.3);
  border-radius: 2px 2px 0 0;
  transition: background 0.2s ease, height 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &.op-histogram-bar-peak {
    background: #8b90a5;
  }
}

.op-histogram-axis {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  font-weight: 600;
  color: #b0b5c8;
  letter-spacing: 0.02em;
}

.op-histogram-axis-mid {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #d0d5dd;
}

@media (max-width: 540px) {
  .op-histogram { padding-left: 0; }
  .op-histogram-axis-mid { display: none; }
}
</style>
